import React, { useState } from 'react';
import styled, { css } from '@emotion/native';
import { BaseText, BodyText } from '@/shared/components/StyledText';
import {
  green,
  lightGreen,
  mint,
  placeholder,
  red,
  secondaryHover,
  white,
} from '@/shared/styles/colors';
import { ProfileFormData } from '@/shared/types/stylist';
import { openImagePicker } from '@/shared/utils';
import { TextInput, Button } from 'react-native-paper';
import useStore from '@/stores/useStore';
import useIsDesktop from '@/shared/hooks/useIsDesktop';
import { IsDesktopProps } from '@/shared/styles/types';
import ErrorFrame from '@/shared/components/ErrorFrame';
import { NAME_MAX_LENGTH, PHONE_NUMBER_MASK } from '@/shared/constants/utility';
import MaskInput from 'react-native-mask-input';
import { FieldTitle } from '@/shared/styles/styledComponents/styledComponents';
import { isValidPhoneNumber } from 'libphonenumber-js';
import StaticInput, { InputLabel } from '@/shared/components/StaticInput';
import { Gender } from '@/shared/types/generated';
import { GENDER_OPTIONS, getGenderLabel } from '@/shared/constants/onboarding';
import Dropdown from '@/shared/components/Dropdown';
import AddIcon from '@/shared/components/AddIcon';
import EditIcon from '@/shared/components/EditIcon';
import BackToTopButton from '@/shared/components/BackToTopButton';
import { useScrollToTop } from '@/shared/hooks/useScrollToTop';
import CustomButton from '@/shared/components/Button';
import TermsToggle from '@/components/terms/TermsToggle';
import TermsModal from '../components/TermsModal';
import SignNowContractSection from '@/components/onboarding/SignNowContractSection';
import DateInput from '@/shared/components/DateInput';
import ShareAgeRadio from '../components/ShareAgeRadio';

interface Props {
  formData: ProfileFormData;
  setFormData: (arg: ProfileFormData) => void;
  phoneNumberError: boolean;
  setPhoneNumberError: (arg: boolean) => void;
  setCheckboxError: (arg: boolean) => void;
  checkboxError: boolean;
  isStylist?: boolean;
  ageError?: boolean;
}

const CreateProfile = ({
  formData,
  setFormData,
  phoneNumberError,
  setPhoneNumberError,
  setCheckboxError,
  checkboxError,
  isStylist,
  ageError = false,
}: Props) => {
  const { setSnackbarState } = useStore();
  const isDesktop = useIsDesktop();
  const scrollToTop = useScrollToTop();

  const [isGenderMenuVisible, setIsGenderMenuVisible] = useState(false);
  const [isPhoneInputFocused, setIsPhoneInputFocused] = useState(false);
  const [isTermsModalVisible, setIsTermsModalVisible] = useState(false);

  const showAgeError = !isStylist && ageError;

  const selectImage = async () => {
    try {
      const profileImage = await openImagePicker();
      if (profileImage) {
        setFormData({ ...formData, profileImage });
      }
    } catch (error) {
      setSnackbarState({
        open: true,
        // @ts-expect-error error is of type unkown but will contain a message property
        message: error.message,
        severity: 'error',
      });
    }
  };

  const handleContractSigned = (documentId: string) => {
    setFormData({ ...formData, signNowDocumentId: documentId });
  };

  return (
    <Container isDesktop={isDesktop}>
      <FieldTitle marginBottom={8}>Profile Image*</FieldTitle>
      <OuterImageContainer>
        <StyledButton
          onPress={selectImage}
          rippleColor={secondaryHover}
          contentStyle={{ width: 211, height: 218 }}
          labelStyle={{ margin: 0, padding: 0 }}
        >
          {formData.profileImage ? (
            <ProfileImageContainer>
              <ProfileImage source={{ uri: formData.profileImage }} />
              <EditButton onPress={selectImage}>
                <EditIcon />
              </EditButton>
            </ProfileImageContainer>
          ) : (
            <AddIcon />
          )}
        </StyledButton>
      </OuterImageContainer>
      <Spacer isDesktop={isDesktop} />
      <FieldTitle marginBottom={12} fontSize={14}>
        Full Name*
      </FieldTitle>
      <InputRow isDesktop={isDesktop}>
        <ErrorFrame
          errorText={`Cannot exceed ${NAME_MAX_LENGTH} characters`}
          errorVisible={formData.firstName.length > NAME_MAX_LENGTH}
          containerStyle={{ marginBottom: 6 }}
        >
          <StaticInput
            value={formData.firstName ?? ''}
            onChange={(e) =>
              setFormData({ ...formData, firstName: e.nativeEvent.text })
            }
            placeholderText="Enter first name here"
            labelText="First Name"
            customStyles={
              isDesktop ? nameInputDesktopStyles : nameInputMobileStyles
            }
          />
        </ErrorFrame>
        <ErrorFrame
          errorText={`Cannot exceed ${NAME_MAX_LENGTH} characters`}
          errorVisible={formData.lastName.length > NAME_MAX_LENGTH}
        >
          <StaticInput
            customStyles={
              isDesktop ? nameInputDesktopStyles : nameInputMobileStyles
            }
            value={formData.lastName ?? ''}
            onChange={(e) =>
              setFormData({ ...formData, lastName: e.nativeEvent.text })
            }
            placeholderText="Enter last name here"
            labelText="Last Name"
          />
        </ErrorFrame>
      </InputRow>

      {isStylist && (
        <>
          <FieldTitle marginBottom={6} fontSize={14}>
            Gender*
          </FieldTitle>
          <DescriptionText>
            This helps clients filter and match with stylists during instant
            styling. Selecting &quot;Prefer not to say&quot; means only clients
            who choose &quot;No Preference&quot; will see you.
          </DescriptionText>
          <Dropdown
            eyebrowText=""
            options={GENDER_OPTIONS}
            selectedOptionLabel={getGenderLabel(formData.gender)}
            selectedOption={formData.gender || 'Select an option'}
            onSelect={(option) =>
              setFormData({ ...formData, gender: option.value as Gender })
            }
            isVisible={isGenderMenuVisible}
            setIsVisible={setIsGenderMenuVisible}
            customDropdownButtonStyle={{ width: '100%' }}
            dropdownTextStyle={{ fontSize: 14 }}
            selectedOptionContentStyle={{ backgroundColor: mint }}
            selectedOptionTextStyle={{ fontWeight: 'bold' }}
          />
        </>
      )}

      <FieldTitle marginBottom={6} fontSize={14}>
        Birthday*
      </FieldTitle>
      <DescriptionText style={{ marginBottom: 16 }}>
        {isStylist
          ? `This helps clients find stylists in their preferred age range. While your birthdate isn't shown publicly, it may be used to match you with clients.`
          : `Your birthday helps us confirm you're 18 or older. This will never be shared publicly.`}
      </DescriptionText>
      <ErrorFrame
        errorText="Oops! It looks like you're not old enough to create an account. Users must be at least 18 years old."
        errorVisible={showAgeError}
        containerStyle={{ marginBottom: showAgeError ? 16 : -16 }}
        errorTextStyle={showAgeError ? birthDateErrorTextStyle : undefined}
      >
        <DateInput
          labelText="Birthday"
          value={formData.birthDate}
          onChange={(date) => setFormData({ ...formData, birthDate: date })}
        />
      </ErrorFrame>

      {!isStylist && (
        <ShareAgeRadio
          value={formData.shareAge ?? false}
          onValueChange={(value) =>
            setFormData({ ...formData, shareAge: value })
          }
          showOptional
          containerStyle={{ marginBottom: 30, marginTop: 0 }}
        />
      )}

      <FieldTitle fontSize={14} marginBottom={10}>
        Terms*
      </FieldTitle>
      <ErrorFrame
        errorVisible={checkboxError}
        errorText="Please accept our Terms of Service to continue"
      >
        <CheckBoxContainer>
          <TermsToggle checked={formData.isCheckBoxChecked} />
          <TermsText>Terms and Conditions Accepted</TermsText>
        </CheckBoxContainer>
        <CustomButton
          onPress={() => setIsTermsModalVisible(true)}
          text="Review Terms & Privacy Policy"
          allowFontScaling={false}
        />
      </ErrorFrame>

      <SignNowContractSection
        isStylist={isStylist}
        onContractSigned={handleContractSigned}
      />

      <FieldTitle marginBottom={6}>Phone Number (Optional)</FieldTitle>
      <DescriptionText>
        We&apos;ll only contact to send important notifications and updates
        about your sessions. Your number won&apos;t be shared with clients or
        third parties.
      </DescriptionText>
      <ErrorFrame
        errorText="Invalid phone number"
        errorVisible={phoneNumberError}
      >
        <InputLabel
          isVisible={Boolean(formData.phoneNumber) || isPhoneInputFocused}
        >
          Phone Number
        </InputLabel>
        <StyledInput
          onBlur={() => setIsPhoneInputFocused(false)}
          onFocus={() => setIsPhoneInputFocused(true)}
          outlineStyle={inputBorderStyle}
          mode="outlined"
          value={formData.phoneNumber}
          placeholder="Enter phone number here"
          placeholderTextColor={placeholder}
          keyboardType="number-pad"
          returnKeyType="done"
          render={(inputProps) => (
            <MaskInput
              {...inputProps}
              style={inputProps.style}
              value={formData.phoneNumber}
              onChangeText={(masked, unmasked) => {
                if (
                  isValidPhoneNumber(`+1${unmasked}`, 'US') ||
                  !unmasked.length
                ) {
                  setPhoneNumberError(false);
                }

                setFormData({
                  ...formData,
                  maskedPhoneNumber: unmasked.length > 0 ? masked : '',
                  phoneNumber: unmasked,
                });
              }}
              mask={PHONE_NUMBER_MASK}
            />
          )}
        />
      </ErrorFrame>
      {!isDesktop && (
        <BackToTopButtonContainer>
          <BackToTopButton scrollToTop={scrollToTop} />
        </BackToTopButtonContainer>
      )}

      <TermsModal
        isVisible={isTermsModalVisible}
        onClose={() => setIsTermsModalVisible(false)}
        acceptTerms={() => {
          setFormData({
            ...formData,
            isCheckBoxChecked: true,
          });
          setCheckboxError(false);
        }}
      />
    </Container>
  );
};

const InputRow = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  flexDirection: isDesktop ? 'row' : 'column',
  justifyContent: 'space-between',
}));

const StyledButton = styled(Button)({
  width: 211,
  height: 218,
  borderRadius: 6,
  backgroundColor: mint,
  justifyContent: 'center',
  alignItems: 'center',
  padding: 0,
});

const ProfileImageContainer = styled.View({
  width: '100%',
  height: '100%',
  position: 'relative',
  justifyContent: 'center',
  alignItems: 'center',
});

const ProfileImage = styled.Image({
  width: 211,
  height: 218,
  borderRadius: 6,
});

const Container = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  flex: 1,
  paddingTop: isDesktop ? 40 : 0,
  paddingLeft: isDesktop ? 40 : 0,
  paddingRight: isDesktop ? 38 : 0,
  paddingBottom: 57,
}));

const OuterImageContainer = styled.View({
  width: '100%',
  alignItems: 'center',
});

const Spacer = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  marginBottom: isDesktop ? 16 : 30,
}));

const StyledInput = styled(TextInput)<IsDesktopProps>({
  height: 50,
  width: '100%',
  color: placeholder,
  justifyContent: 'center',
  paddingLeft: 10,
  backgroundColor: white,
});

const inputBorderStyle = css({
  borderWidth: 1,
  borderColor: mint,
  borderRadius: 36,
});

const nameInputDesktopStyles = css({
  width: 234,
  marginBottom: 0,
});

const nameInputMobileStyles = css({
  width: '100%',
  marginBottom: 0,
});

const DescriptionText = styled(BodyText)({
  marginBottom: 21,
  fontSize: 12,
  color: lightGreen,
  lineHeight: 16,
});

const EditButton = styled.TouchableOpacity({
  position: 'absolute',
  bottom: 0,
  right: 0,
  borderRadius: 6,
});

const TermsText = styled(BaseText)({
  fontWeight: 400,
  fontSize: 14,
  letterSpacing: 0.84,
  color: green,
  marginBottom: 0,
  marginLeft: 11,
});

const CheckBoxContainer = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  paddingRight: 28,
  marginBottom: 18,
});

const BackToTopButtonContainer = styled.View({
  marginTop: 20,
});

const birthDateErrorTextStyle = css({
  fontSize: 12,
  letterSpacing: -0.12,
  fontWeight: 'bold',
  color: red,
  marginTop: 8,
  marginBottom: 8,
});

export default CreateProfile;
